import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Resolution, IProjectAsset } from '@packages/global';
import { novelUploadApi } from '../../../services/user';
import { projectSettingGenerationApi } from '../../../services/user/projectSettingGeneration.api';
import { useToast } from '../../../stores/toastStore';

export interface ScriptIdeaSelectedModels {
  llmModelId: string;
  llmVlModelId: string;
  text2imageModelId: string;
  imageEditModelId: string;
}

export function useScriptIdea() {
  const navigate = useNavigate();
  const [scriptContent, setScriptContent] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [loading, setLoading] = useState(false);

  // 小说文件
  const [novelFileUrl, setNovelFileUrl] = useState('');
  const [novelFileName, setNovelFileName] = useState('');
  const [novelUploading, setNovelUploading] = useState(false);
  const novelFileInputRef = useRef<HTMLInputElement>(null);

  // 模型选择
  const [selectedModels, setSelectedModels] = useState<ScriptIdeaSelectedModels>({
    llmModelId: '',
    llmVlModelId: '',
    text2imageModelId: '',
    imageEditModelId: '',
  });

  // 视频属性
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState<Resolution>(Resolution.HD_720P);

  // 单个分镜最大时长（秒），null 表示不限制
  const [maxShotDuration, setMaxShotDuration] = useState<number | null>(null);

  // 预设主体
  const [selectedSubjects, setSelectedSubjects] = useState<IProjectAsset[]>([]);
  const addSubjects = (assets: IProjectAsset[]) => {
    setSelectedSubjects(prev => {
      const existingIds = new Set(prev.map(a => a.id));
      return [...prev, ...assets.filter(a => !existingIds.has(a.id))];
    });
  };
  const removeSubject = (id: string) => setSelectedSubjects(prev => prev.filter(a => a.id !== id));

  const handleNovelFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['.txt', '.md', '.docx'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      useToast.getState().error('不支持的文件格式，请上传 TXT、MD 或 DOCX 文件');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      useToast.getState().error('文件大小不能超过 50MB');
      return;
    }

    setNovelUploading(true);
    try {
      const formData = new FormData();
      formData.append('novel', file);
      const result = await novelUploadApi.uploadNovel(formData);
      setNovelFileUrl(result.url);
      setNovelFileName(file.name);
      useToast.getState().success('小说文件上传成功');
    } catch (error: any) {
      useToast.getState().error(error.message || '上传失败，请重试');
    } finally {
      setNovelUploading(false);
      if (novelFileInputRef.current) novelFileInputRef.current.value = '';
    }
  };

  const handleClearNovelFile = () => {
    setNovelFileUrl('');
    setNovelFileName('');
  };

  const handleSubmit = async () => {
    if (!scriptContent.trim() && !novelFileUrl) {
      alert('请输入您的剧本创意或上传小说文件');
      return;
    }
    setLoading(true);
    try {
      const styleNote = selectedStyle ? `\n\n【风格偏好】${selectedStyle}` : '';
      const result = await projectSettingGenerationApi.startAutoGeneration({
        scriptIdea: scriptContent.trim() + styleNote,
        novelFileUrl: novelFileUrl || undefined,
        aspectRatio,
        resolution,
        llmModelId: selectedModels.llmModelId || undefined,
        llmVlModelId: selectedModels.llmVlModelId || undefined,
        text2imageModelId: selectedModels.text2imageModelId || undefined,
        imageEditModelId: selectedModels.imageEditModelId || undefined,
        presetSubjectIds: selectedSubjects.length > 0 ? selectedSubjects.map(s => s.id) : undefined,
        maxShotDuration: maxShotDuration ?? undefined,
      });
      navigate(`/project/${result.projectId}`);
    } catch (error: any) {
      useToast.getState().error(error.message || '创建失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !!(scriptContent.trim() || novelFileUrl);

  return {
    scriptContent, setScriptContent,
    selectedStyle, setSelectedStyle,
    loading,
    novelFileUrl, novelFileName, novelUploading, novelFileInputRef,
    handleNovelFileUpload, handleClearNovelFile,
    selectedModels, setSelectedModels,
    aspectRatio, setAspectRatio,
    resolution, setResolution,
    maxShotDuration, setMaxShotDuration,
    selectedSubjects, addSubjects, removeSubject,
    handleSubmit,
    canSubmit
  };
}
